import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NumberInput,
  Select,
  MultiSelect as MantineMultiSelect,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { localDateTimeToISO } from "../../../../../utils/datetime";
import { IconDeviceFloppy, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { TableSkeleton } from "../../../components/SkeletonLoader";
import {
  CUSTOM_FIELD_DEFINITION_FRAGMENT,
  CustomFieldType,
  CustomFieldWithValue,
} from "../shared/customFieldFragments";
import { ImageFieldUpload } from "./ImageFieldUpload";

const CUSTOM_FIELD_VALUES_QUERY = gql`
  query customFieldValues($objectType: ObjectType!, $objectId: ID!) {
    customFieldValues(objectType: $objectType, objectId: $objectId) {
      definition {
        ...CustomFieldDefinitionFields
      }
      value
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

const SET_CUSTOM_FIELD_VALUES = gql`
  mutation setCustomFieldValues($input: SetCustomFieldValuesInput!) {
    setCustomFieldValues(input: $input) {
      definition {
        ...CustomFieldDefinitionFields
      }
      value
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

interface CustomFieldValuesPanelProps {
  objectType: string;
  objectId: string;
}

export const CustomFieldValuesPanel = ({
  objectType,
  objectId,
}: CustomFieldValuesPanelProps) => {
  const { data, loading, refetch } = useQuery<{
    customFieldValues: CustomFieldWithValue[];
  }>(CUSTOM_FIELD_VALUES_QUERY, {
    variables: { objectType, objectId },
  });

  const [setValues] = useMutation(SET_CUSTOM_FIELD_VALUES);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const fields = data?.customFieldValues ?? [];
  const hasChanges = Object.keys(editedValues).length > 0;

  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleDeleteKey = (key: string, fieldName: string) => {
    modals.openConfirmModal({
      title: "Delete field value",
      centered: true,
      children: (
        <Text size="sm">
          Delete the stored value for <strong>{fieldName}</strong>? This cannot
          be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          const result = await setValues({
            variables: {
              input: {
                objectType,
                objectId,
                fields: [],
                deleteKeys: [key],
              },
            },
          });
          if (result?.error) throw new Error(result.error.message);
          refetch();
          notifications.show({
            title: "Value deleted",
            message: `Custom field value for "${fieldName}" has been removed`,
            color: "green",
            autoClose: 3000,
          });
        } catch (error) {
          notifications.show({
            title: "Delete failed",
            message:
              error instanceof Error && error.message.length < 200
                ? error.message
                : "Could not delete value",
            color: "red",
            autoClose: 5000,
          });
        }
      },
    });
  };

  const handleImmediateSave = async (key: string, value: string) => {
    try {
      const result = await setValues({
        variables: {
          input: {
            objectType,
            objectId,
            fields: [{ key, value }],
          },
        },
      });
      if (result?.error) {
        throw new Error(result.error.message);
      }
      refetch();
      notifications.show({
        title: "Value saved",
        message: "Custom field value has been updated",
        color: "green",
        autoClose: 3000,
      });
    } catch (error) {
      notifications.show({
        title: "Save failed",
        message:
          error instanceof Error && error.message.length < 200
            ? error.message
            : "Could not save value",
        color: "red",
        autoClose: 5000,
      });
    }
  };

  const validateFieldValue = (
    type: CustomFieldType,
    value: string,
  ): string | null => {
    if (!value) return null;
    switch (type) {
      case CustomFieldType.EMAIL:
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Invalid email address";
        break;
      case CustomFieldType.URL:
        try {
          const parsed = new URL(value);
          if (!["https:", "http:"].includes(parsed.protocol))
            return "URL must use http:// or https://";
        } catch {
          return "Invalid URL";
        }
        break;
      case CustomFieldType.PHONE:
        if (!/^\+?[\d\s\-().]{7,20}$/.test(value))
          return "Invalid phone number";
        break;
      case CustomFieldType.COUNTRY:
        if (!/^[A-Z]{2,3}$/.test(value))
          return "Invalid country code (use ISO 3166-1 alpha-2, e.g. US)";
        break;
    }
    return null;
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    // Validate typed fields before saving
    for (const [key, value] of Object.entries(editedValues)) {
      const field = fields.find((f) => f.definition.key === key);
      if (field) {
        const validationError = validateFieldValue(
          field.definition.type as CustomFieldType,
          value,
        );
        if (validationError) {
          notifications.show({
            title: `Validation error: ${field.definition.name}`,
            message: validationError,
            color: "red",
            autoClose: 5000,
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      const result = await setValues({
        variables: {
          input: {
            objectType,
            objectId,
            fields: Object.entries(editedValues).map(([key, value]) => ({
              key,
              value,
            })),
          },
        },
      });
      if (result?.error) {
        throw new Error(result.error.message);
      }
      setEditedValues({});
      refetch();
      notifications.show({
        title: "Values saved",
        message: "Custom field values have been updated",
        color: "green",
        autoClose: 3000,
      });
    } catch (error) {
      notifications.show({
        title: "Save failed",
        message:
          error instanceof Error && error.message.length < 200
            ? error.message
            : "Could not save values",
        color: "red",
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={3} columns={2} />;
  }

  if (fields.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No custom fields defined for this object type.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Custom Fields</Title>
        {hasChanges && (
          <Button
            size="xs"
            leftSection={<IconDeviceFloppy size={14} />}
            loading={saving}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        )}
      </Group>

      {fields.map((field) => {
        const def = field.definition;
        const currentValue =
          editedValues[def.key] !== undefined
            ? editedValues[def.key]
            : (field.value ?? "");

        return (
          <Card key={def.id} p="sm" withBorder>
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <Text fw={500} size="sm">
                  {def.name}
                </Text>
                <Badge size="xs" variant="light">
                  {def.type}
                </Badge>
                {def.required && (
                  <Badge size="xs" color="red" variant="light">
                    Required
                  </Badge>
                )}
              </Group>
              {!def.required &&
                field.value !== undefined &&
                field.value !== null && (
                  <Tooltip label="Delete stored value">
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => handleDeleteKey(def.key, def.name)}
                    >
                      <IconTrash size={13} />
                    </ActionIcon>
                  </Tooltip>
                )}
            </Group>

            {def.description && (
              <Text size="xs" c="dimmed" mb="xs">
                {def.description}
              </Text>
            )}

            <FieldValueInput
              type={def.type as CustomFieldType}
              value={currentValue}
              options={def.options}
              onChange={(val) => handleValueChange(def.key, val)}
              onImmediateSave={(val: string) =>
                handleImmediateSave(def.key, val)
              }
              objectType={objectType}
              fieldKey={def.key}
            />
          </Card>
        );
      })}
    </Stack>
  );
};

interface FieldValueInputProps {
  type: CustomFieldType;
  value: string;
  options?: { label: string; value: string }[];
  onChange: (value: string) => void;
  onImmediateSave: (value: string) => Promise<void>;
  objectType: string;
  fieldKey: string;
}

const FieldValueInput = ({
  type,
  value,
  options,
  onChange,
  onImmediateSave,
  objectType,
  fieldKey,
}: FieldValueInputProps) => {
  switch (type) {
    case CustomFieldType.STRING:
      return (
        <TextInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          size="sm"
          maxLength={4096}
        />
      );

    case CustomFieldType.NUMBER:
      return (
        <NumberInput
          value={value ? Number(value) : undefined}
          onChange={(val) => onChange(val?.toString() ?? "")}
          placeholder="Enter number"
          size="sm"
        />
      );

    case CustomFieldType.BOOLEAN:
      return (
        <Checkbox
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          label={value === "true" ? "True" : "False"}
        />
      );

    case CustomFieldType.DATE:
      return (
        <DateTimePicker
          value={value || null}
          onChange={(date) => onChange(localDateTimeToISO(date))}
          placeholder="Select date"
          size="sm"
          clearable
        />
      );

    case CustomFieldType.EMAIL:
      return (
        <TextInput
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter email"
          size="sm"
        />
      );

    case CustomFieldType.PHONE:
      return (
        <TextInput
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter phone number"
          size="sm"
        />
      );

    case CustomFieldType.URL:
      return (
        <TextInput
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter URL"
          size="sm"
        />
      );

    case CustomFieldType.COUNTRY:
      return (
        <TextInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter country code (e.g. US, FR)"
          size="sm"
        />
      );

    case CustomFieldType.SELECT:
      return (
        <Select
          data={options ?? []}
          value={value || null}
          onChange={(val) => onChange(val ?? "")}
          placeholder="Select an option"
          size="sm"
          clearable
          searchable
        />
      );

    case CustomFieldType.MULTI_SELECT: {
      const selectedValues = value ? value.split(",").filter(Boolean) : [];
      return (
        <MantineMultiSelect
          data={options ?? []}
          value={selectedValues}
          onChange={(vals) => onChange(vals.join(","))}
          placeholder="Select options"
          size="sm"
          clearable
          searchable
        />
      );
    }

    case CustomFieldType.IMAGE:
      return (
        <ImageFieldUpload
          value={value}
          onChange={onChange}
          onImmediateSave={onImmediateSave}
          objectType={objectType}
          fieldKey={fieldKey}
        />
      );

    default:
      return (
        <TextInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          size="sm"
        />
      );
  }
};
